import api from './api'
import store from '@/store'

/**
 * @typedef DataForQrCodeWithAuthName
 * @property {DataForGenerateQrCode} params Данные для отрисовки QR кода
 * @property {string} name Название аутентификации (например - Промежуточная аутентификация)
 */

const checkinApi = config =>
  api({
    headers: {
      'X-Timezone': store.getters.timezone || 'Europe/Moscow',
    },
    ...config,
  })

/**
 * Метод для обычной QR аутентификации, чтобы выйти на смену
 *
 * @param {DataForGenerateQrCode} checkinData Данные о сгенерированном QR коде
 * @param {string} token Токен для запросов на апи CRM
 * @returns {string} message Сообщение о произошедшей QR аутентификации
 */
export const checkinQr = async ({ checkinData, token }) => {
  const {
    data: { message },
  } = await checkinApi({
    method: 'post',
    url: '/api/crm/checkin/qr',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: checkinData,
  })

  return message
}

/**
 * Метод для "общей" QR аутентификации (один QR для всех), чтобы выйти на смену
 *
 * @param {DataForGenerateQrCode} checkinData Данные о сгенерированном "общем" QR коде
 * @param {string} token Токен для запросов на апи CRM
 * @returns {string} message Сообщение о произошедшей QR аутентификации
 */
export const checkinGeneralQr = async ({ checkinData, token }) => {
  const {
    data: { message },
  } = await checkinApi({
    method: 'post',
    url: '/api/crm/checkin/general/approve',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: checkinData,
  })

  return message
}

/**
 * Получение данных для отрисовки QR кода
 *
 * @param {object} employer Данные о сгенерированном "общем" QR коде
 * @param {number} employer.scheduleIdForCheckin Id смены для которой генерируется QR
 * @param {string} checkinType Тип чекина (qr, sms, push)
 * @param {object} extraParams Данные для бека, чтобы он мог обрабатывать редкиекейсы (сейчас это только ранний уход)
 * @returns {DataForQrCodeWithAuthName}
 */
export const generateQr = async ({ employer, checkinType, extraParams = {} }) => {
  const {
    data: { params, name },
  } = await checkinApi({
    method: 'post',
    url: '/api/crm/checkin',
    data: {
      ...extraParams,
      employer_schedule_id: employer.scheduleIdForCheckin,
      type: checkinType,
    },
  })

  return { params, name }
}

/**
 * Получение данных для отрисовки "общего" QR кода
 *
 * @param {string} token Токен для запросов на апи CRM
 * @returns {DataForGenerateQrCode}
 */
export const generateGeneralQr = async token => {
  const {
    data: { params },
  } = await checkinApi({
    method: 'post',
    url: '/api/crm/checkin/general/qr',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return params
}

export const getStatuses = async employersIds => {
  const {
    data: { checkins, intermediate_checks: intermediateCheckins },
  } = await checkinApi({
    method: 'get',
    url: '/api/crm/checkin',
    params: {
      employer_ids: employersIds,
    },
  })

  return { checkins, intermediateCheckins }
}

export const getStatusOfQr = async qrId => {
  const {
    data: { message },
  } = await checkinApi({
    method: 'get',
    url: `/api/crm/checkin/qr/status/${qrId}`,
  })

  return message
}

export const getStatusOfGeneralQr = async ({ qrId, token, cancelToken }) => {
  const {
    data: { name: message },
  } = await checkinApi({
    method: 'get',
    url: `/api/crm/checkin/general/status/${qrId}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cancelToken,
  })

  return message
}

export const authByCode = async ({ code, hash, id }) => {
  const {
    data: { message },
  } = await checkinApi({
    method: 'post',
    url: '/api/crm/checkin/sms',
    data: {
      code: parseInt(code, 10),
      hash,
      id,
    },
  })

  return message
}

export const giveBreak = employerId =>
  checkinApi({
    method: 'post',
    url: '/api/crm/checkin/break',
    data: {
      employer_id: employerId,
    },
  })

export const checkQrsPermission = async token => {
  const {
    data: {
      result: { 'master-qr': userHasMasterQr },
    },
  } = await checkinApi({
    method: 'get',
    url: '/api/crm/checkin/has-master',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return userHasMasterQr
}

export const createManualCheckin = manualCheckinData =>
  checkinApi({
    method: 'post',
    url: '/api/crm/checkin/manual',
    data: manualCheckinData,
  })

export const updateManualCheckin = ({ manualCheckinData, checkinId }) =>
  checkinApi({
    method: 'put',
    url: `/api/crm/checkin/manual/${checkinId}`,
    data: manualCheckinData,
  })
